import { useLocation, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";

const FootwearStudio = lazy(() => import("./design/FootwearStudio"));
const CollectionPlanPage = lazy(
  () => import("@/features/footwear-studio/collectionPlan/CollectionPlanPage"),
);
const RulesLibrary = lazy(() => import("./footwear-studio/RulesLibrary"));

const PageLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center space-y-3">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

export default function NotFound() {
  const { pathname } = useLocation();

  // Retired studio URLs → canonical entry
  if (
    pathname === "/footwear-studio-classic" ||
    pathname.startsWith("/footwear-studio-wizard") ||
    pathname.startsWith("/footwear-studio-v2")
  ) {
    return <Navigate to="/footwear-studio" replace />;
  }

  // HARD BLOCK: Prevent Builder.io from hijacking app-only routes
  // These routes MUST render the real studio components, never Builder
  // If Builder intercepts and routes reach NotFound, we render the classic studio directly
  const appOnlyRoutes = [
    "/footwear-studio",
    "/design/footwear",
    "/design/footwear-studio",
    "/design/footwear-studio/moodboard",
    "/design/moodboard",
    "/footwear-studio/moodboard",
    "/footwear-studio/collection-plan",
    "/footwear-studio/rules",
    "/rules",
  ];

  // Check if this is a footwear-studio route that Builder might have hijacked
  if (appOnlyRoutes.some(route => pathname === route || pathname.startsWith(route + "/"))) {
    // Rules Library - handle both /footwear-studio/rules and /rules alias
    if (pathname === "/rules" || pathname === "/footwear-studio/rules" || pathname.startsWith("/footwear-studio/rules/")) {
      return (
        <Suspense fallback={<PageLoadingFallback />}>
          <RulesLibrary />
        </Suspense>
      );
    }
    // If Builder intercepted /footwear-studio, render the classic studio directly
    if (pathname === "/footwear-studio" || pathname.startsWith("/footwear-studio/")) {
      if (pathname.startsWith("/footwear-studio/moodboard")) {
        return <Navigate to="/footwear-studio/collection-plan" replace />;
      }
      if (pathname.startsWith("/footwear-studio/collection-plan")) {
        return (
          <Suspense fallback={<PageLoadingFallback />}>
            <CollectionPlanPage />
          </Suspense>
        );
      }
      // Render classic studio directly - no redirect to avoid loops
      return (
        <Suspense fallback={<PageLoadingFallback />}>
          <FootwearStudio />
        </Suspense>
      );
    }
    if (
      pathname === "/design/footwear-studio/moodboard" ||
      pathname === "/design/moodboard" ||
      pathname.startsWith("/design/footwear-studio/moodboard/") ||
      pathname.startsWith("/design/moodboard/")
    ) {
      return <Navigate to="/footwear-studio/collection-plan" replace />;
    }
    // Default to main studio route
    return <Navigate to="/footwear-studio" replace />;
  }

  // Hard bypasses that kill the 404 for old Studio pages:
  if (
    pathname === "/design/footwear-studio/moodboard" ||
    pathname === "/design/moodboard"
  ) {
    return <Navigate to="/footwear-studio/collection-plan" replace />;
  }
  if (pathname === "/design/footwear-studio") {
    return <Navigate to="/footwear-studio" replace />;
  }

  // Your original 404 UI
  return (
    <div style={{ padding: 32 }}>
      <h1>This STRONOM route is not defined yet.</h1>
      <p>
        The address {pathname} does not correspond to an active module or
        resource in the STRONOM Suite.
      </p>
      <a href="/">Return to the homepage</a>
    </div>
  );
}
